import { Submission, SubmissionSender } from "@fitko/fit-connect";
import type {
  EventLogEntry,
  GetEventLogResponse,
  GetRepliesOfCaseResponse,
  GetStoredSubmissionsResponse,
  GetSubmissionStatusResponse,
  RefreshRepliesResponse,
  Reply,
  RoutingDestination,
  SearchDestinationsResponse,
  SendableSubmission,
  SendSubmissionResponse,
  StoredSubmission,
  SubmissionStatus,
} from "@/types/fitconnect";
import { JWK } from "jose";
import { ForkedApiService } from "@/lib/forkedApiService.ts";

const KEYS_PATH = "/api/fitconnect/keys/{destinationId}";
const SUBMISSION_PATH = "/api/fitconnect/submit";

const normalizeBaseUrl = (url: string) => url.replace(/\/$/, "");

const getServiceUrl = (): string => {
  const fromEnv = import.meta.env.VITE_FITCONNECT_SERVICE_URL as
    string | undefined;
  if (!fromEnv || !fromEnv.trim()) {
    return ""; // defaults to be hosted by spring boot
  }
  return normalizeBaseUrl(fromEnv);
};

const createSender = (usedKid?: string): SubmissionSender => {
  const base = getServiceUrl();
  const suffix = usedKid ? `?usedKid=${usedKid}` : "";
  return new SubmissionSender(
    {
      publicKeyUrl: `${base}${KEYS_PATH}`,
      submissionUrl: `${base}${SUBMISSION_PATH}${suffix}`,
    },
    new ForkedApiService(),
  );
};

const extractErrorMessage = (error) => {
  if (error?.response?.data?.message) {
    return error?.response?.data?.message;
  } else if (error.message) {
    return error.message;
  } else {
    return JSON.stringify(error);
  }
};

export const sendMessage = async (
  submissionInput: SendableSubmission,
): Promise<SendSubmissionResponse> => {
  if (!submissionInput.content) {
    throw new Error("Invalid message format");
  }
  if (submissionInput.enableReply && !submissionInput.replyProcessStandard) {
    throw new Error(
      "Must set a replyProcessStandard when replies are enabled.",
    );
  }

  const api = new ForkedApiService();
  const timestamp = new Date().toISOString();

  try {
    const builder = Submission.builder()
      .setDestination(submissionInput.subject)
      .setServiceType(submissionInput.serviceIdentifier || "", "Test");

    let sender: SubmissionSender;
    if (submissionInput.enableReply) {
      const publicReplyKey = await api.post<JWK>(
        `${getServiceUrl()}/api/fitconnect/generate-session-key`,
        new FormData(),
      );
      builder.setReplyChannel({
        fitConnect: {
          encryptionPublicKey: publicReplyKey,
          processStandards: [submissionInput.replyProcessStandard],
        },
      });
      sender = createSender(publicReplyKey.kid);
    } else {
      sender = createSender();
    }

    const submission = submissionInput.isXml
      ? builder.setXmlData(submissionInput.content, submissionInput.schemaUri)
      : builder.setJsonData(submissionInput.content, submissionInput.schemaUri);

    submission.addAttachments(submissionInput.attachments);

    const sentSubmission = await sender.sendSubmission(submission.build());

    if (!sentSubmission) {
      return {
        success: false,
        error: "Failed to send message, server error",
        timestamp,
      };
    }

    return {
      success: true,
      caseId: sentSubmission.caseId,
      submissionId: sentSubmission.submissionId,
      status: sentSubmission.status.toUpperCase(),
      timestamp,
    };
  } catch (error) {
    console.error("Error sending message:", error);

    return {
      success: false,
      error: extractErrorMessage(error),
      timestamp,
    };
  }
};

export const getStoredSubmissions = async (
  onlyReplyEnabledOnes: boolean,
): Promise<GetStoredSubmissionsResponse> => {
  const api = new ForkedApiService();
  try {
    const res = await api.get<StoredSubmission[]>(
      `${getServiceUrl()}/api/fitconnect/submissions?onlyReplyEnabledOnes=${onlyReplyEnabledOnes}`,
    );
    return {
      error: null,
      submissions: res,
    };
  } catch (error) {
    console.error("Failed to retrieve stored submissions:", error);
    return {
      error: extractErrorMessage(error),
      submissions: [],
    };
  }
};

export const getSubmissionEventLog = async (
  submissionId: string,
): Promise<GetEventLogResponse> => {
  const api = new ForkedApiService();
  try {
    const res = await api.get<EventLogEntry[]>(
      `${getServiceUrl()}/api/fitconnect/submissions/${submissionId}/events`,
    );
    return {
      error: null,
      eventLogEntries: res,
    };
  } catch (error) {
    console.error(
      `Failed to retrieve event logs for submission ${submissionId}`,
      error,
    );
    return {
      error: extractErrorMessage(error),
      eventLogEntries: [],
    };
  }
};

export const getSubmissionStatus = async (
  submissionId: string,
): Promise<GetSubmissionStatusResponse> => {
  const api = new ForkedApiService();
  try {
    const res = await api.get<SubmissionStatus>(
      `${getServiceUrl()}/api/fitconnect/submissions/${submissionId}/status`,
    );
    return {
      error: null,
      status: res,
    };
  } catch (error) {
    console.error(
      `Failed to retrieve status for submission ${submissionId}`,
      error,
    );
    return {
      error: extractErrorMessage(error),
      status: null,
    };
  }
};

export const searchDestinations = async (
  ars: string,
  leika: string,
): Promise<SearchDestinationsResponse> => {
  const api = new ForkedApiService();
  try {
    const res = await api.get<{ items: RoutingDestination[] }>(
      `${getServiceUrl()}/api/fitconnect/search?ars=${ars}&leika=${leika}`,
    );
    return {
      error: null,
      destinations: res.items,
    };
  } catch (error) {
    console.error(
      `Failed to search destinations for ars ${ars} and leika ${leika}`,
      error,
    );
    return {
      error: extractErrorMessage(error),
      destinations: [],
    };
  }
};

/** URL to download an attachment of a reply; the session cookie authorizes the request. */
export const replyAttachmentUrl = (replyId: string, attachmentId: string) =>
  `${getServiceUrl()}/api/fitconnect/replies/${replyId}/attachments/${attachmentId}`;

export const getRepliesOfCase = async (
  caseId: string,
): Promise<GetRepliesOfCaseResponse> => {
  const api = new ForkedApiService();
  try {
    const res = await api.get<Reply[]>(
      `${getServiceUrl()}/api/fitconnect/cases/${caseId}/replies`,
    );
    return {
      error: null,
      replies: res,
    };
  } catch (error) {
    console.error(`Failed to retrieve replies for case ${caseId}`, error);
    return {
      error: extractErrorMessage(error),
      replies: [],
    };
  }
};

export const refreshReplies = async (): Promise<RefreshRepliesResponse> => {
  const api = new ForkedApiService();
  try {
    const res = await api.post<number>(
      `${getServiceUrl()}/api/fitconnect/refresh-replies`,
      new FormData(),
    );
    return {
      error: null,
      refreshedReplies: res,
    };
  } catch (error) {
    console.error("Failed to refresh replies", error);
    return {
      error: extractErrorMessage(error),
      refreshedReplies: 0,
    };
  }
};
